import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import axios from 'axios';
import './EmbeddedTalentForm.css';
import astronautCongratulate from '../../assets/poses/astronout-standing-congratulate.svg';
import PhoneInput from '../../components/inputs/PhoneInput/PhoneInput';

const EmbeddedTalentForm = () => {
  // Partner token from the environment variable
  const partnerToken = process.env.REACT_APP_PARTNER_TOKEN;
  // External webhook URL from the environment variable
  const webhookUrl = process.env.REACT_APP_WEBHOOK_URL_GT;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    seniority_level: ''
  });

  const [utmParams, setUtmParams] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [authUrl, setAuthUrl] = useState('');

  // Seniority level options
  const seniorityOptions = [
    { value: 'assistant', label: 'Auxiliar' },
    { value: 'associate', label: 'Assistente' },
    { value: 'junior_analyst', label: 'Analista Jr.' },
    { value: 'analyst', label: 'Analista Pleno' },
    { value: 'senior_analyst', label: 'Analista Sênior' },
    { value: 'coordinator', label: 'Coordenador' },
    { value: 'manager', label: 'Gerente' },
    { value: 'director', label: 'Diretor' },
    { value: 'c_level', label: 'C-Level' }
  ];

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const params = {};

    const utmKeys = [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_content',
      'utm_term',
      'fbclid',
      'gclid',
      'wbraid',
      'gbraid'
    ];

    utmKeys.forEach(key => {
      const value = searchParams.get(key);
      if (value) {
        params[key] = value;
      }
    });

    setUtmParams(params);
  }, []);

  useEffect(() => {
    const sendHeightToParent = () => {
      if (window.parent !== window) {
        const height = document.documentElement.scrollHeight;
        window.parent.postMessage({
          type: 'resize',
          height: height
        }, '*');
      }
    };

    sendHeightToParent();
    window.addEventListener('resize', sendHeightToParent);

    const observer = new MutationObserver(sendHeightToParent);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true
    });

    return () => {
      window.removeEventListener('resize', sendHeightToParent);
      observer.disconnect();
    };
  }, [showSuccess, message]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setMessage('Nome é obrigatório');
      setMessageType('error');
      return false;
    }

    // Validate full name (at least two words)
    const nameWords = formData.name.trim().split(/\s+/);
    if (nameWords.length < 2 || !nameWords.every(word => word.length > 0)) {
      setMessage('Digite seu nome completo (nome e sobrenome)');
      setMessageType('error');
      return false;
    }

    if (!formData.email.trim()) {
      setMessage('Email é obrigatório');
      setMessageType('error');
      return false;
    }

    if (!formData.phone.trim()) {
      setMessage('Telefone é obrigatório');
      setMessageType('error');
      return false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setMessage('Email inválido');
      setMessageType('error');
      return false;
    }

    // Phone validation (must have at least 11 digits)
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (phoneDigits.length < 11) {
      setMessage('Telefone deve ter pelo menos 11 dígitos');
      setMessageType('error');
      return false;
    }

    if (!formData.seniority_level) {
      setMessage('Nível de senioridade é obrigatório');
      setMessageType('error');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Check if the token is configured
    if (!partnerToken) {
      setMessage('Token de parceiro não configurado');
      setMessageType('error');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      // Prepare data for the API
      const talentData = {
        talent: {
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          seniority_level: formData.seniority_level,
          ...utmParams
        }
      };

      // Send the data to the external API using the api.js configuration
      const response = await api.post('/external/talents', talentData, {
        headers: {
          'Authorization': `Bearer ${partnerToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data && response.data.data && response.data.data.attributes) {
        const { authUrl: responseAuthUrl } = response.data.data.attributes;

        // GTM EVENT
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'formSubmissionSuccess',
          email: formData.email,
        });

        // Send data to external webhook if the URL is configured
        if (webhookUrl) {
          try {
            await axios.post(webhookUrl, talentData, {
              headers: {
                'Content-Type': 'application/json',
              },
              // Add timeout to avoid hanging requests
              timeout: 5000
            });
          } catch (webhookError) {
            console.warn('Error sending to external webhook:', webhookError);
            // Don't interrupt the main flow if the webhook fails
          }
        }

        setMessage('Cadastro realizado com sucesso!');
        setMessageType('success');
        setShowSuccess(true);
        setAuthUrl(responseAuthUrl || '');
      }

    } catch (error) {
      console.error('Erro ao cadastrar talento:', error);

      if (error.response) {
        const { status, data } = error.response;

        let errorMessages = [];
        if (data && Array.isArray(data.errors) && data.errors.length > 0) {
          errorMessages = data.errors;
        } else if (data && data.message) {
          errorMessages = [data.message];
        }

        switch (status) {
          case 400:
            setMessage(errorMessages.join('\n'));
            break;
          case 401:
            setMessage('Token de autenticação inválido ou expirado.');
            break;
          case 409:
            setMessage(errorMessages.join('\n'));
            break;
          case 422:
            setMessage(errorMessages.join('\n'));
            break;
          default:
            setMessage('Erro interno do servidor, tente novamente mais tarde.');
        }
      } else {
        setMessage('Erro de conexão. Verifique sua internet e tente novamente.');
      }

      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleLoginClick = () => {
    if (authUrl) {
      window.open(authUrl, '_blank');
    }
  };

  // Show success screen
  if (showSuccess) {
    return (
      <div className="embedded-talent-form">
        <div className="success-screen">
          <div className="success-icon">
            <img src={astronautCongratulate} alt="Astronauta celebrando" />
          </div>
          <h2>Cadastro Realizado!</h2>
          <p>Parabéns! Seu cadastro foi realizado com sucesso na eComLovers.</p>

          {authUrl ? (
            <div className="login-section">
              <button
                onClick={handleLoginClick}
                className="login-btn"
              >
                Acessar a App
              </button>
            </div>
          ) : (
            <div className="login-section">
              <p>Você receberá um email com instruções para acessar sua conta.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="embedded-talent-form">
      <div className="form-header">
        <h2>Cadastro</h2>
        <p>Preencha seus dados para se cadastrar na eComLovers</p>
      </div>

      <form onSubmit={handleSubmit} className="talent-form">
        <div className="form-section">
          {message && (
            <div className={`message ${messageType}`}>
              {message}
            </div>
          )}
          <div className="form-group">
            <label htmlFor="name">Nome Completo *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Digite seu nome completo"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="seu@email.com"
              required
            />
          </div>

          <div className="form-group">
            <PhoneInput
              name="phone"
              label="Telefone *"
              value={formData.phone}
              onChange={(value) => setFormData(prev => ({ ...prev, phone: value }))}
              placeholder="(XX) XXXXX-XXXX"
              error={null}
            />
          </div>

          <div className="form-group">
            <label htmlFor="seniority_level">Nível de Senioridade *</label>
            <select
              id="seniority_level"
              name="seniority_level"
              value={formData.seniority_level}
              onChange={handleInputChange}
              required
              className="seniority-select"
            >
              <option value="">Selecione seu nível</option>
              {seniorityOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="submit-btn"
            disabled={loading}
          >
            {loading ? 'Cadastrando...' : 'Cadastrar'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EmbeddedTalentForm;
